import React, { useState } from "react";
import {
  Typography,
  Tag,
  Button,
  Row,
  Col,
  Modal,
  Avatar,
  Divider,
  Space,
  Card,
  Tooltip,
} from "antd";
import {
  // CloseOutlined,
  CalendarOutlined,
  BookOutlined,
  UserOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { POST_TYPES } from "Pages/MyBatches/Channels/ChannelComponents/ChannelFormComponents/PostDetails";
import { capitalizeFirstLetter } from "Pages/StudentPortfolio/StudentCommentSection/StudentCommentListingBox";
import CustomModal from "Components/CommonComponents/CustomModal";
import PDFPageviewer from "Pages/PlannerView/PDFPageviewer";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { statusColor } from "Pages/MyBatches/Channels/ChannelComponents/SinglePostScreen";
import "./index.css";
import QuestionTextProvider from "Components/CommonComponents/QuestionTextProvider";
import { cdnUrl } from "utilFunctions/Constants/urlConstants";
import { resolveAssetUrl } from "utilFunctions/shared/fileUtils";
const { Title, Text } = Typography;

interface PostViewScreenProps {
  selectedPost: any;
  onEdit?: () => void;
  onDelete: () => void;
  onCancel: () => void;
  isPostLoading?: boolean;
  onDeleteAttachment?: (attachmentId: string) => void;
  refreshCommentsTrigger?: number;
  hasFullAccess?: boolean;
}

const PostViewScreen: React.FC<PostViewScreenProps> = ({
  selectedPost,
  onCancel,
  onDeleteAttachment,
  hasFullAccess,
}) => {
  const { screen } = useWindowSize();
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
  const [selectedPdfName, setSelectedPdfName] = useState("");
  const [selectedAssetType, setSelectedAssetType] = useState<
    "pdf" | "image" | null
  >(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const handleViewPdf = (url: string, name: string) => {
    setSelectedPdfUrl(url);
    setSelectedPdfName(name);
    setSelectedAssetType("pdf");
    setPdfModalVisible(true);
  };

  const handleViewImage = (url: string, name: string) => {
    setSelectedPdfUrl(url);
    setSelectedPdfName(name);
    setSelectedAssetType("image");
    setPdfModalVisible(true);
  };

  const handleConfirmDelete = () => {
    setDeleteModalVisible(false);
  };
  const renderAttachments = () => {
    if (!selectedPost?.content || selectedPost.content.length === 0) {
      return <Text type="secondary">No attachments</Text>;
    }

    return (
      <Row gutter={[16, 16]}>
        {selectedPost.content.map((attachment: any) => (
          <Col key={attachment.id} xs={12} sm={12} md={8} lg={6}>
            <Card
              size="small"
              actions={[
                <Tooltip title="View" key={attachment.id + "view"}>
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      if (attachment.assetType === "Ebook") {
                        handleViewPdf(
                          resolveAssetUrl(attachment.url, cdnUrl),
                          attachment.url.split("/").pop() || "PDF"
                        );
                      } else {
                        handleViewImage(
                          resolveAssetUrl(attachment.url, cdnUrl),
                          attachment.url.split("/").pop() || "Image"
                        );
                      }
                    }}
                  />
                </Tooltip>,
                onDeleteAttachment && (
                  <Tooltip title="Delete" key={attachment.id + "Delete"}>
                    <Button
                      type="text"
                      danger
                      disabled={
                        !hasFullAccess || selectedPost?.status == "PUBLISHED"
                      }
                      icon={<DeleteOutlined />}
                      onClick={() => onDeleteAttachment(attachment.id)}
                    />
                  </Tooltip>
                ),
              ].filter(Boolean)}
            >
              <div style={{ textAlign: "center" }}>
                {attachment.assetType === "Ebook" ? (
                  <FilePdfOutlined
                    style={{ fontSize: "24px", color: "#ff4d4f" }}
                  />
                ) : (
                  <FileImageOutlined
                    style={{ fontSize: "24px", color: "#1890ff" }}
                  />
                )}
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "12px",
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    height: "36px",
                  }}
                >
                  {attachment.name
                    ? attachment.name || `${attachment.assetType} File`
                    : ""}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };
  const formatBatchIds = (batchIds: any) => {
    if (!batchIds) return "N/A";
    const batchData = batchIds.map((el: any) => el.label);
    return batchData.join(", ");
  };
  console.log("PostViewScreen selectedPost:", selectedPost);
  return (
    <>
      <Modal
        open={true}
        onCancel={onCancel}
        footer={null}
        centered
        width={1000}
        closable={true}
        className="postview-custom-modalBody"
        bodyStyle={{
          padding: "0px 16px",
          backgroundColor: "#fff",
          borderRadius: "16px",
          overflowY: "auto",
          maxHeight: "80vh",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        destroyOnClose={true}
      >
        {/* Header Section */}
        <div style={{ position: "relative", marginBottom: "24px" }}>
          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Avatar
              size={48}
              style={{
                backgroundColor: "#1890ff",
                fontWeight: "600",
                fontSize: "18px",
              }}
              icon={<UserOutlined />}
            >
              {selectedPost?.userEmail || "U"}
            </Avatar>
            <div style={{ flex: 1 }}>
              <Title
                level={3}
                style={{
                  margin: "0 0 4px 0",
                  fontFamily: "Inter, Roboto, sans-serif",
                  fontWeight: 600,
                  fontSize: "24px",
                  lineHeight: "32px",
                }}
              >
                {selectedPost?.title || ""}
              </Title>
              <Space size="middle" align="center">
                <div style={{ marginBottom: "8px" }}>
                  <Text style={{ fontSize: "16px", color: "#666" }}>
                    {selectedPost?.userEmail || ""}
                  </Text>
                </div>
                <Tag color={statusColor(selectedPost?.status)}>
                  {selectedPost?.status || "Published"}
                </Tag>
              </Space>
            </div>
          </div>
          <Divider style={{ margin: "16px 0", borderColor: "#f0f0f0" }} />
        </div>

        {/* Assignment Info Row */}
        <div
          style={{
            display: "flex",
            gap: "32px",
            padding: "16px 16px",
            marginBottom: "24px",
            fontSize: "14px",
            color: "#666",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              textTransform: "capitalize",
            }}
          >
            <CalendarOutlined />
            {selectedPost?.status == "DRAFT"
              ? "Updated"
              : selectedPost?.status.toLowerCase() || "Published"}
            :&nbsp;
            {selectedPost?.scheduledAt
              ? dayjs(selectedPost.scheduledAt).format("DD-MMM-YYYY HH:mm")
              : dayjs(selectedPost.updatedAt).format("DD-MMM-YYYY HH:mm")}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <BookOutlined />
            Type:{" "}
            {selectedPost?.postType
              ? POST_TYPES.find((el: any) => el.value === selectedPost.postType)
                  ?.label ?? "Assignment"
              : "Assignment"}
          </span>
        </div>

        {/* Assignment Description */}
        <div style={{ marginBottom: "32px" }}>
          <Title
            level={4}
            style={{
              marginBottom: "12px",
              fontFamily: "Inter, Roboto, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
            }}
          >
            Description
          </Title>
          <div
            className="channel-rich-content"
            style={{
              border: "1px solid #e5e5e5",
              borderRadius: "12px",
              padding: "20px",
              lineHeight: "1.6",
              fontSize: "14px",
              color: "#666",
            }}
          >
            <QuestionTextProvider
              content={selectedPost?.description || ""}
            ></QuestionTextProvider>
          </div>
        </div>
        {/* Content Attachments */}
        {selectedPost?.content && selectedPost.content.length > 0 && (
          <div style={{ marginBottom: "32px" }}>
            <Title
              level={4}
              style={{
                marginBottom: "12px",
                fontFamily: "Inter, Roboto, sans-serif",
                fontWeight: 600,
                fontSize: "18px",
              }}
            >
              Attachments
            </Title>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {renderAttachments()}
            </div>
          </div>
        )}
        {/* Assignment Metadata */}
        <div
          style={{
            marginBottom: "32px",
          }}
        >
          <Title
            level={4}
            style={{
              fontWeight: 600,
              fontSize: "18px",
              marginBottom: "12px",
            }}
          >
            Metadata
          </Title>
          <Row
            gutter={[32, 16]}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "12px",
              padding: "16px",
              margin: 0,
            }}
          >
            <Col span={12}>
              <div style={{ display: "flex" }}>
                <Text
                  strong
                  style={{
                    fontSize: "14px",
                    width: "150px",
                  }}
                >
                  Created Date:
                </Text>
                <Text style={{ fontSize: "14px", color: "#666" }}>
                  {selectedPost?.updatedAt
                    ? dayjs(selectedPost.updatedAt).format("YYYY-MM-DD")
                    : "2024-10-08"}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: "flex" }}>
                <Text
                  strong
                  style={{
                    fontSize: "14px",
                    width: "150px",
                  }}
                >
                  Subject:
                </Text>
                <Text style={{ fontSize: "14px", color: "#666" }}>
                  {capitalizeFirstLetter(selectedPost?.subject || "Physics")}
                </Text>
              </div>
            </Col>
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: "14px",
                    minWidth: "150px",
                    width: "150px",
                  }}
                >
                  Target Batches:
                </Text>
                <Text style={{ fontSize: "14px", color: "#666" }}>
                  {formatBatchIds(selectedPost?.initialBatchOptions || [])}
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Footer */}
        <div
          style={{
            textAlign: "end",
            paddingTop: "16px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button type="primary" onClick={onCancel}>
            Close
          </Button>
        </div>
      </Modal>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete this post? This action cannot be
          undone.
        </p>
      </Modal>

      <CustomModal
        modal={pdfModalVisible}
        centered={true}
        backdrop={true}
        size={"xl"}
        fullscreen={
          selectedAssetType === "pdf" ||
          ["xl", "lg", "md", "xxl"].includes(screen)
        }
        className={
          selectedAssetType === "pdf"
            ? "document-viewer-fullscreen-modal"
            : undefined
        }
        toggle={() => {
          setPdfModalVisible(false);
          setSelectedPdfName("");
          setSelectedPdfUrl("");
          setSelectedAssetType(null);
        }}
        title={selectedPdfName}
      >
        {selectedPdfUrl && selectedPdfUrl != "" && (
          <div
            className="modern-asset-viewer-container w-100 d-flex align-items-center justify-content-center"
            style={{
              height: "100vh",
              maxHeight: "100vh",
              overflow: "hidden",
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              padding: "0",
            }}
          >
            {selectedAssetType === "pdf" ? (
              <PDFPageviewer
                pdfUrl={selectedPdfUrl}
                allPageInOne={true}
                isLibraryView={true}
                parentComponent="ViewPost"
              />
            ) : (
              <img
                src={selectedPdfUrl}
                alt={selectedPdfName || "Attachment preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
        )}
      </CustomModal>
    </>
  );
};

export default PostViewScreen;
